import "./AppAside.scss";
import {NavLink, useLocation, useNavigate} from "react-router-dom";
import {env} from "../env/env";
import LocationDropdown from "./LocationDropdown";
import {LocationDto, MemberDto, MemberDtoPlanEnum} from "../generated/dashboard-client";
import React, {useCallback, useEffect, useState} from "react";
import {useRecoilValue} from "recoil";
import {
  memberState
} from "../state/atoms";
import ItemLibraryIcon from "./icons/ItemLibraryIcon";
import OrderingMenuIcon from "./icons/OrderingMenuIcon";
import MyBrandIcon from "./icons/MyBrandIcon";

import {
  AppstoreOutlined,
  CreditCardOutlined,
  DollarCircleOutlined,
  PrinterOutlined,
  QuestionCircleOutlined,
  SettingOutlined,
  StarFilled,
  UserOutlined,
} from "@ant-design/icons";
import ChangeLocationIcon from "./icons/ChangeLocationIcon";
import {Button} from "antd";
import {OmInUseModal} from "./om/OmInUseModal";
import {HELP_CENTER_URL, homePaths, isTestEnv} from "../util/constants";
import {isAccountPath, isContentPath, isDSAPath, isLandingPath, isPrintPath, synchronousRequest} from "../util/utils";
import DisplayIconBlack from "./icons/DisplayIconBlack";
import ListingsIcon from "./icons/ListingsIcon";
import { MemberDtoRoleEnum } from "../generated/dashboard-client";
import { isDesignSubscription, isProSubscription } from "../service/BillingService";
import DiningRDIcon from "./icons/DiningRDIcon";
import BrandKitIcon from "./icons/BrandKitIcon";
import LinkPagesIconJun26 from "./icons/LinkPagesIconJun26";
import { useBrandDestination } from "../service/useBrandDestination";
import { useListingsAccess } from "../hooks/useListingsAccess";

interface AppAsideProps {
  activeLocation: LocationDto;
  isMarketingBannerPage: boolean;
  users: MemberDto[];
}

enum AsideMode {
  Dashboard,
  Account,
  Hidden,
}

export default function AppAside({activeLocation, isMarketingBannerPage, users}: AppAsideProps) {
  const location = useLocation();
  const member = useRecoilValue(memberState);
  const listingsAllowed = useListingsAccess() === true;
  const isPro = isProSubscription(member);
  const [mode, setMode] = useState(AsideMode.Dashboard);
  const [isOoLoading, setOoLoading] = useState(false);
  const [isOoInUse, setOoInUse] = useState(false);
  const navigate = useNavigate();
  const {
    destinationFor: brandDestinationFor,
    goToBrand,
    ensureKits: ensureBrandKits,
    canManage: canManageBrand,
  } = useBrandDestination();

  useEffect(() => {
    if (canManageBrand) ensureBrandKits();
  }, [canManageBrand, ensureBrandKits]);

  function goToTeam() {
    window.location.href = `${env.dashboardPath}/account/team`;
  }

  function goPro() {
    if (!member || !member.id) return;
    window.location.href = env.dashboardPath + "/account/plan";
  }

  const onOnlineMenuClick = useCallback(() => {
    if (!isProSubscription(member)) {
      navigate(`${env.dashboardPath}/online-menus/`);
      return;
    }

    setOoLoading(true);
    setTimeout(() => {
      const resp = synchronousRequest(`/app/online/location/stats/${activeLocation.internalId!}?oo=true`);
      let location: any | null = null;
      if (resp) {
        try {
          location = JSON.parse(JSON.parse(resp).data).location;
        } catch (e) {
          console.error(e);
        }
      }

      if (location?.inUse) {
        setOoInUse(true);
        setOoLoading(false);
      } else {
        const base = isTestEnv ? 'https://test.mhmfun.com' : 'https://ohbz.com';//window.location.href.indexOf("staging.") > -1 ? "https://www.musthavemenus.com" : env.basePath;
        const tkn = window.localStorage.getItem("tkn");
        window.open(`${base}${env.ooContextPath}?businessId=${activeLocation.internalId}&tkn=${tkn}`, '_blank');
        setOoLoading(false);
      }
    }, 200);
  }, [activeLocation.internalId, navigate, member])

  // Design-plan accounts don't include Business Listings — open the payment modal
  // (Add Business Listings add-on flow) instead of routing to the Listings page.
  function onListingsClick(e: React.MouseEvent) {
    if (!isDesignSubscription(member)) return;
    e.preventDefault();
    window.MHM.ProModal.addListings({
      memberId: `${member.ownerId}` || member.id,
      locationId: activeLocation.internalId,
      onSuccess: () => window.location.reload(),
    });
  }

  useEffect(() => {
    const {pathname} = location;

    if (isPrintPath(pathname) || isLandingPath(pathname) || isContentPath(pathname) || isDSAPath(pathname)) {
      setMode(AsideMode.Hidden);
    } else if (isAccountPath(pathname)) {
      setMode(AsideMode.Account);
    } else {
      setMode(AsideMode.Dashboard);
    }
  }, [location]);

  return (
    <>
      {mode !== AsideMode.Hidden && (
        <aside className={"main"} style={{top: !isMarketingBannerPage ? '80px' : ''}}>
          {mode === AsideMode.Dashboard && (
            <>
              <LocationDropdown location={activeLocation} users={users} />
              <nav>
                <ul>
                  <li className="label">Menu Management</li>

                  <li>
                    <NavLink
                      to={`${env.dashboardPath}/designs/${activeLocation.internalId}`}
                      className={
                        homePaths.includes(window.location.pathname)
                          ? "active"
                          : ""
                      }
                    >
                      <AppstoreOutlined/>
                      Menu Center
                    </NavLink>
                  </li>

                  {/* dataOnly is the per-location opt-out from the Item Library on an account that
                      otherwise has it: those users edit menu data in the editor, so the Item Library
                      section is not theirs to see. Timber hides its Item Library tab for them too. */}
                  {!member.viewOnly && !member.updateDrd && !member.dataOnly && member.showItemLibrary && (
                    <li className={!isPro ? "gray-out disallowed" : ""}>
                      <NavLink onClick={(event) => {
                          if (!isPro) {
                            event.preventDefault();
                          }
                        }}
                        aria-disabled={!isPro}
                        to={`${env.dashboardPath}/item-library`}
                        className={
                          window.location.pathname.indexOf("item-library") > -1
                            ? "active"
                            : ""
                        }
                      >
                        <ItemLibraryIcon />
                        <span style={{ margin: 0 }}>Item Library</span>
                        {!isPro && (
                          <img
                            src="https://timber.mhmcdn.com/site/marketing/icons/ProPricingStar.svg"
                            alt="pro star"
                          />
                        )}
                      </NavLink>
                    </li>
                  )}

                  {false && activeLocation.hasBrandTemplates && (
                    <li>
                      <NavLink
                        to={`${env.dashboardPath}/brand-templates/${activeLocation.internalId}`}
                        className={
                          window.location.pathname.indexOf("brand-templates") > -1
                            ? "active"
                            : ""
                        }
                      >
                        <BrandKitIcon/>
                        Brand Templates
                      </NavLink>
                    </li>
                  )}

                  {!member.viewOnly && !member.itemLibraryOnly && !member.dataOnly && !member.updateDrd &&
                  <li>
                    <NavLink
                      className={/(^|\/)brand(\/|$)/.test(location.pathname) ? "active" : ""}
                      to={brandDestinationFor(activeLocation)}
                      onClick={(e) => {
                        e.preventDefault();
                        goToBrand(activeLocation);
                      }}
                    >
                      <MyBrandIcon/>
                      <span>Brand</span>
                    </NavLink>
                  </li>}

                  <li className="label">Channels</li>

                  {!member.viewOnly && !member.itemLibraryOnly && !member.dataOnly && !member.updateDrd && !listingsAllowed &&
                  <li>
                    <NavLink
                      className={location.pathname.indexOf("listings-coming-soon") > -1 ? "active" : ""}
                      to={`${env.dashboardPath}/listings-coming-soon`}
                    >
                      <ListingsIcon/>
                      <span>Listings</span>
                      <span className="coming-soon-badge">Coming soon!</span>
                    </NavLink>
                  </li>}

                  {!member.viewOnly && !member.itemLibraryOnly && !member.dataOnly && !member.updateDrd && listingsAllowed &&
                  <li className={!isProSubscription(member) ? 'gray-out' : ''}>
                    <NavLink
                      className={location.pathname.indexOf("listings") > -1 && location.pathname.indexOf("listings-coming-soon") === -1 ? "active" : ""}
                      to={`${env.dashboardPath}/listings/${activeLocation.internalId}`}
                      onClick={onListingsClick}
                    >
                      <ListingsIcon/>
                      <span>Listings</span>
                      {!isProSubscription(member) &&
                      <img src="https://timber.mhmcdn.com/site/marketing/icons/ProPricingStar.svg" alt="pro star"/>}
                    </NavLink>
                  </li>}

                  {!member.viewOnly && !member.itemLibraryOnly && !member.dataOnly && !member.updateDrd &&
                  <li className={!isProSubscription(member) ? 'gray-out' : ''}>
                    <NavLink to={`${env.dashboardPath}/dsa/manage`}>
                      <DisplayIconBlack/>
                      <span style={{margin: 0}}>Display</span>
                      {!isProSubscription(member) &&
                      <img src="https://timber.mhmcdn.com/site/marketing/icons/ProPricingStar.svg" alt="pro star"/>}
                    </NavLink>
                  </li>}

                  {!member.viewOnly && !member.itemLibraryOnly && !member.dataOnly && !member.updateDrd &&
                  <li className={!isProSubscription(member) ? 'gray-out' : ''}>
                    <Button
                      onClick={onOnlineMenuClick}
                      type={"link"}
                      loading={isOoLoading}
                      className={location.pathname.indexOf("online-menus") > -1 ? "active" : ""}
                    >
                      {!isOoLoading && <OrderingMenuIcon/>}
                      <span>Mobile Menu</span>
                      {!isProSubscription(member) &&
                      <img src="https://timber.mhmcdn.com/site/marketing/icons/ProPricingStar.svg" alt="pro star"/>}
                    </Button>
                  </li>}
                  {!member.viewOnly && !member.itemLibraryOnly && !member.dataOnly && !member.updateDrd &&
                  <li className={!isProSubscription(member) ? 'gray-out' : ''}>
                    <NavLink
                      className={location.pathname.indexOf("link-pages") > -1 ? "active" : ""}
                      to={`${env.dashboardPath}/link-pages/${activeLocation.internalId}`}
                    >
                      <LinkPagesIconJun26/>
                      <span>Link Pages</span>
                      {!isProSubscription(member) &&
                      <img src="https://timber.mhmcdn.com/site/marketing/icons/ProPricingStar.svg" alt="pro star"/>}
                    </NavLink>
                  </li>}
                  <li>
                    <a href={HELP_CENTER_URL} target="_blank" rel="noreferrer">
                      <QuestionCircleOutlined/>
                      Help Center
                    </a>
                  </li>
                </ul>
              </nav>

              <footer>
                {(member.owner || member.role === "Admin" as MemberDtoRoleEnum) && (
                  <div className={"btn-container"}>
                    {!isProSubscription(member) ? (
                      <Button onClick={goPro} className={"go-pro"}>
                        <StarFilled/>
                        Upgrade Now
                      </Button>
                    ) : (
                      <Button onClick={goToTeam} className={"secondary-two add-team"}>
                        <UserOutlined/>
                        Add Your Team
                      </Button>
                    )}
                  </div>
                )}
              </footer>
            </>
          )}

          {mode === AsideMode.Account && (
            <>
              <nav className={"account-aside"}>
                <ul>
                  <li>
                    <NavLink
                      className={"user-info"}
                      to={env.dashboardPath + "/account/users"}
                    >
                      <SettingOutlined/>
                      User Info
                    </NavLink>
                  </li>
                  {member.plan !== MemberDtoPlanEnum.Free && (member.owner || member.role === "Admin") && (
                    <li>
                      <NavLink
                        className={"billing-info"}
                        to={env.dashboardPath + "/account/billing"}
                      >
                        <CreditCardOutlined/>
                        Billing Info
                      </NavLink>
                    </li>
                  )}
                  {(member.owner || member.role === "Admin") && (
                    <li>
                      <NavLink
                        className={"subscription"}
                        to={env.dashboardPath + "/account/plan"}
                      >
                        <DollarCircleOutlined/>
                        Subscription
                      </NavLink>
                    </li>
                  )}
                  {(member.owner || member.role === "Admin") && (
                    <li className={!isProSubscription(member) && !member.diningRDIntegration ? 'gray-out' : ''}>
                      <NavLink
                        className={"locations"}
                        to={env.dashboardPath + "/account/locations"}
                      >
                        <ChangeLocationIcon/>
                        <span>Locations</span>
                        {!isProSubscription(member) && !member.diningRDIntegration &&
                        <img src="https://timber.mhmcdn.com/site/marketing/icons/ProPricingStar.svg" alt="pro star"/>}
                      </NavLink>
                    </li>
                  )}
                  {(member.owner || member.role === "Admin") && (
                    <li className={!isProSubscription(member) && !member.diningRDIntegration ? 'gray-out' : ''}>
                      <NavLink
                        className={"team"}
                        to={env.dashboardPath + "/account/team"}
                      >
                        <UserOutlined/>
                        <span>Team</span>
                        {!isProSubscription(member) && !member.diningRDIntegration &&
                        <img src="https://timber.mhmcdn.com/site/marketing/icons/ProPricingStar.svg" alt="pro star"/>}
                      </NavLink>
                    </li>
                  )}
                  {(member.owner || member.role === "Admin" || (member as any).manageBrand) && (
                  <li>
                    <NavLink
                      className={({ isActive }) =>
                        "brand-kit" + (isActive || location.pathname.startsWith(env.dashboardPath + "/account/brand-management") ? " active" : "")
                      }
                      to={env.dashboardPath + "/account/brand-management"}
                    >
                      <BrandKitIcon/>
                      <span>Brand Management</span>
                    </NavLink>
                  </li>
                  )}
                  {(member.owner || member.role === "Admin") && (
                    <li>
                      <NavLink
                        className={"print-history"}
                        to={env.dashboardPath + "/account/print"}
                      >
                        <PrinterOutlined/>
                        Print History
                      </NavLink>
                    </li>
                  )}
                  {(member.owner || member.role === "Admin") && member.diningRDIntegration && (
                    <li>
                      <NavLink
                        className={"drd"}
                        to={env.dashboardPath + "/account/drd"}
                      >
                        <DiningRDIcon/>
                        DiningRD Integration
                      </NavLink>
                    </li>
                  )}
                </ul>
              </nav>
            </>
          )}
        </aside>
      )}

      <OmInUseModal
        isVisible={isOoInUse}
        onClose={() => {
          setOoInUse(false);
          setOoLoading(false);
        }}
      />
    </>
  );
}
